/**
 * Airbnb Listing - Complete JavaScript Functionality
 * Handles all modals, gallery, booking, and interactive features
 *
 * GALLERY CHANGE: modal now uses vertical scroll instead of
 * left/right arrow navigation. All images are stacked top-to-bottom;
 * users scroll down to browse. Thumbnail strip stays at the bottom —
 * clicking a thumbnail smoothly scrolls to that image.
 **/

document.addEventListener('DOMContentLoaded', function() {
    console.log('Airbnb Listing JavaScript loaded');

    // FIX: Icon Loading
    console.log('=== ICON LOADING FIX ===');
    const iconImages = document.querySelectorAll('.detail-icon-img');
    iconImages.forEach((img, index) => {
        const defaultIcon = img.nextElementSibling;
        img.style.display = 'block';
        if (defaultIcon) defaultIcon.style.display = 'none';

        img.addEventListener('load', () => {
            img.style.display = 'block';
            if (defaultIcon) defaultIcon.style.display = 'none';
        });
        img.addEventListener('error', () => {
            img.style.display = 'none';
            if (defaultIcon) defaultIcon.style.display = 'block';
        });
        if (img.complete) {
            if (img.naturalHeight !== 0) {
                img.style.display = 'block';
                if (defaultIcon) defaultIcon.style.display = 'none';
            } else {
                img.style.display = 'none';
                if (defaultIcon) defaultIcon.style.display = 'block';
            }
        }
    });

    const locationIconImg = document.querySelector('.location-icon-img');
    if (locationIconImg) {
        const locationDefaultIcon = document.querySelector('.default-location-icon');
        locationIconImg.style.display = 'block';
        if (locationDefaultIcon) locationDefaultIcon.style.display = 'none';

        locationIconImg.addEventListener('load', () => {
            locationIconImg.style.display = 'block';
            if (locationDefaultIcon) locationDefaultIcon.style.display = 'none';
        });
        locationIconImg.addEventListener('error', () => {
            locationIconImg.style.display = 'none';
            if (locationDefaultIcon) locationDefaultIcon.style.display = 'block';
        });
        if (locationIconImg.complete) {
            if (locationIconImg.naturalHeight !== 0) {
                locationIconImg.style.display = 'block';
                if (locationDefaultIcon) locationDefaultIcon.style.display = 'none';
            } else {
                locationIconImg.style.display = 'none';
                if (locationDefaultIcon) locationDefaultIcon.style.display = 'block';
            }
        }
    }

    // Initialize all functionality
    initializeGalleryModal();
    initializeInlineGallery();
    initializeGuestModal();
    initializeShowMoreButtons();
    initializeHostAwayBooking();
    initializeMobileFeatures();
    initializeAdminIconUpload();
    initializeInteractiveMap();
});

/* ================================================================
   GALLERY MODAL — VERTICAL SCROLL VERSION
   All images are stacked vertically. Users scroll down to browse.
   Clicking a thumbnail scrolls to that image in the main area.
   IntersectionObserver keeps the counter + active thumbnail in sync.
   ================================================================ */
function initializeGalleryModal() {
    const galleryModal     = document.getElementById('gallery-modal');
    const galleryTriggers  = document.querySelectorAll('[data-gallery-trigger], .photos-count-btn');
    const closeBtn         = document.getElementById('gallery-modal-close');
    const scrollContainer  = document.getElementById('gallery-modal-scroll');
    const currentIndexEl   = document.getElementById('gallery-current-index');
    const totalCountEl     = document.getElementById('gallery-total-count');

    let images        = [];
    let scrollObs     = null;  // IntersectionObserver instance

    /* ── Collect all product images ────────────────────────── */
    function collectAllImages() {
        const seen   = new Set();
        const result = [];

        function add(src, alt) {
            const hi = src.replace(/width=\d+/, 'width=1200');
            if (!seen.has(hi)) { seen.add(hi); result.push({ src: hi, alt: alt || '' }); }
        }

        // Primary: modal thumbnails contain the full Liquid-rendered image list
        document.querySelectorAll('#gallery-thumbnails img').forEach(img => {
            if (img.src) add(img.src, img.alt);
        });

        // Fallback 1: main gallery image + page thumbnail images
        if (result.length === 0) {
            const mainImg = document.querySelector('.gallery-main-image');
            if (mainImg?.src) add(mainImg.src, mainImg.alt);
            document.querySelectorAll('.gallery-thumbnail-image').forEach(img => {
                if (img.src) add(img.src, img.alt);
            });
        }

        // Fallback 2: any Shopify CDN images on the page
        if (result.length === 0) {
            document.querySelectorAll('img[src*="cdn.shopify.com"]').forEach(img => {
                if (img.src && !img.src.includes('placeholder')) add(img.src, img.alt);
            });
        }

        console.log('Gallery: collected', result.length, 'images');
        return result;
    }

    images = collectAllImages();
    if (totalCountEl) totalCountEl.textContent = images.length;

    /* ── Build the vertical image stack in #gallery-modal-scroll ── */
    function buildScrollGallery() {
        if (!scrollContainer) return;
        scrollContainer.innerHTML = '';

        images.forEach((image, index) => {
            const item = document.createElement('div');
            item.className   = 'gallery-scroll-item';
            item.dataset.index = String(index);

            const img     = document.createElement('img');
            img.src        = image.src;
            img.alt        = image.alt || `Photo ${index + 1}`;
            img.loading    = index < 3 ? 'eager' : 'lazy';
            img.decoding   = 'async';

            item.appendChild(img);
            scrollContainer.appendChild(item);
        });

        rebuildThumbnails();
        setupScrollObserver();
    }

    /* ── Rebuild thumbnail strip to match collected images ──── */
    function rebuildThumbnails() {
        const container = document.getElementById('gallery-thumbnails');
        if (!container) return;
        container.innerHTML = '';

        images.forEach((image, index) => {
            const item = document.createElement('div');
            item.className   = 'gallery-thumbnail-item' + (index === 0 ? ' active' : '');
            item.dataset.index = String(index);

            const img  = document.createElement('img');
            img.src    = image.src.replace(/width=\d+/, 'width=120');
            img.alt    = image.alt || `Thumbnail ${index + 1}`;
            img.loading = 'lazy';

            item.appendChild(img);
            container.appendChild(item);

            // Click → smooth-scroll main area to that image
            item.addEventListener('click', () => {
                const target = scrollContainer?.querySelector(
                    `.gallery-scroll-item[data-index="${index}"]`
                );
                if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
            });
        });
    }

    /* ── IntersectionObserver: update counter + active thumb ── */
    function setupScrollObserver() {
        if (scrollObs) scrollObs.disconnect();
        if (!scrollContainer) return;

        scrollObs = new IntersectionObserver(entries => {
            // Pick the most-visible entry
            let best = null;
            entries.forEach(e => {
                if (e.isIntersecting) {
                    if (!best || e.intersectionRatio > best.intersectionRatio) best = e;
                }
            });
            if (!best) return;

            const idx = parseInt(best.target.dataset.index, 10);
            if (currentIndexEl) currentIndexEl.textContent = idx + 1;
            updateActiveThumbnail(idx);
        }, {
            root: scrollContainer,
            threshold: [0.25, 0.5, 0.75],
        });

        scrollContainer.querySelectorAll('.gallery-scroll-item').forEach(item => {
            scrollObs.observe(item);
        });
    }

    /* ── Keep thumbnail strip in sync with scroll position ──── */
    function updateActiveThumbnail(index) {
        const thumbs = document.querySelectorAll('#gallery-thumbnails .gallery-thumbnail-item');
        thumbs.forEach((t, i) => t.classList.toggle('active', i === index));

        const active = thumbs[index];
        if (active) active.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }

    /* ── Open gallery at a specific image index ──────────────── */
    function openGallery(startIndex = 0) {
        startIndex = Math.max(0, Math.min(startIndex, images.length - 1));

        buildScrollGallery();

        galleryModal.classList.add('active');
        galleryModal.style.display = 'block';
        document.body.style.overflow = 'hidden';

        // Two rAF passes ensure layout is complete before we scroll
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                const target = scrollContainer?.querySelector(
                    `.gallery-scroll-item[data-index="${startIndex}"]`
                );
                if (target) target.scrollIntoView({ behavior: 'instant', block: 'start' });
                if (currentIndexEl) currentIndexEl.textContent = startIndex + 1;
                updateActiveThumbnail(startIndex);
            });
        });
    }

    /* ── Close gallery ───────────────────────────────────────── */
    function closeGallery() {
        galleryModal.classList.remove('active');
        galleryModal.style.display = 'none';
        document.body.style.overflow = '';
        if (scrollObs) { scrollObs.disconnect(); scrollObs = null; }
    }

    /* ── Keyboard navigation ─────────────────────────────────── */
    document.addEventListener('keydown', e => {
        if (!galleryModal.classList.contains('active')) return;

        if (e.key === 'Escape') { closeGallery(); return; }

        const current = parseInt(currentIndexEl?.textContent || '1', 10) - 1;

        // ArrowDown / ArrowRight → next image
        if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
            e.preventDefault();
            const next = scrollContainer?.querySelector(
                `.gallery-scroll-item[data-index="${current + 1}"]`
            );
            if (next) next.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }

        // ArrowUp / ArrowLeft → previous image
        if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
            e.preventDefault();
            const prev = scrollContainer?.querySelector(
                `.gallery-scroll-item[data-index="${current - 1}"]`
            );
            if (prev) prev.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    });

    /* ── Gallery trigger clicks ──────────────────────────────── */
    galleryTriggers.forEach(trigger => {
        trigger.addEventListener('click', e => {
            // On mobile, don't open modal for inline gallery items
            if (window.innerWidth <= 767 &&
                (trigger.classList.contains('gallery-main-item') ||
                 trigger.classList.contains('gallery-thumbnail-item'))) {
                e.preventDefault(); e.stopPropagation(); return;
            }

            e.preventDefault(); e.stopPropagation();

            let startIndex = 0;
            if (trigger.dataset.index !== undefined) {
                startIndex = parseInt(trigger.dataset.index, 10);
            } else if (trigger.classList.contains('gallery-thumbnail-item')) {
                startIndex = Array.from(
                    document.querySelectorAll('.gallery-thumbnail-item')
                ).indexOf(trigger) + 1;
            }

            console.log('Gallery trigger clicked, opening at index', startIndex);
            openGallery(startIndex);
        });
    });

    /* ── Close button & overlay ──────────────────────────────── */
    if (closeBtn) closeBtn.addEventListener('click', closeGallery);

    galleryModal.addEventListener('click', e => {
        if (e.target === galleryModal ||
            e.target.classList.contains('gallery-modal-overlay')) {
            closeGallery();
        }
    });

    /* ── Global compat shims (scroll to next/prev) ───────────── */
    window.galleryNextImage = function () {
        const current = parseInt(currentIndexEl?.textContent || '1', 10) - 1;
        const next = scrollContainer?.querySelector(
            `.gallery-scroll-item[data-index="${current + 1}"]`
        );
        if (next) next.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    window.galleryPrevImage = function () {
        const current = parseInt(currentIndexEl?.textContent || '1', 10) - 1;
        const prev = scrollContainer?.querySelector(
            `.gallery-scroll-item[data-index="${current - 1}"]`
        );
        if (prev) prev.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    console.log('Gallery modal ready — vertical scroll mode,', images.length, 'images');
}

/* ================================================================
   INLINE GALLERY
   Handles the page-level image carousel.
   Mobile additions:
     • Prev / Next arrow buttons overlaid on the image
     • Thumbnail strip below the image (scrollable, all images,
       4 visible at a time) — syncs with carousel position
   ================================================================ */
function initializeInlineGallery() {
    let currentMainIndex = 0;
    const mainContainer  = document.querySelector('.gallery-main-container');
    const thumbnailItems = document.querySelectorAll('.gallery-thumbnail-item');

    if (!mainContainer) { console.log('Inline gallery container not found'); return; }

    /* ── Collect images ── */
    const allImages = [];
    const mainImage = document.querySelector('.gallery-main-image');
    if (mainImage?.src) allImages.push(mainImage.src.replace(/width=\d+/, 'width=800'));
    thumbnailItems.forEach(thumb => {
        const img = thumb.querySelector('img, .gallery-thumbnail-image');
        if (img?.src && img.src !== mainImage?.src)
            allImages.push(img.src.replace(/width=\d+/, 'width=800'));
    });
    if (allImages.length === 1) {
        document.querySelectorAll('#gallery-thumbnails img').forEach(img => {
            if (img.src && !allImages.includes(img.src))
                allImages.push(img.src.replace(/width=\d+/, 'width=800'));
        });
    }

    if (allImages.length > 1) createSlidingCarousel();

    function createSlidingCarousel() {
        /* ── Carousel shell ── */
        const carouselContainer = document.createElement('div');
        carouselContainer.className = 'gallery-carousel';
        carouselContainer.style.cssText =
            'position:relative;width:100%;height:100%;overflow:hidden;border-radius:12px;';

        /* ── Slides ── */
        const slidesContainer = document.createElement('div');
        slidesContainer.className = 'gallery-slides';
        slidesContainer.style.cssText = `
            display:flex;
            width:${allImages.length * 100}%;
            height:100%;
            transition:transform 0.3s cubic-bezier(0.25,0.46,0.45,0.94);
            will-change:transform;
        `;
        allImages.forEach((src, i) => {
            const slide = document.createElement('div');
            slide.style.cssText =
                `flex:0 0 ${100/allImages.length}%;width:${100/allImages.length}%;height:100%;`;
            const img = document.createElement('img');
            img.src = src;
            img.alt = `Gallery image ${i + 1}`;
            img.style.cssText =
                'width:100%;height:100%;object-fit:cover;object-position:center;display:block;';
            slide.appendChild(img);
            slidesContainer.appendChild(slide);
        });
        carouselContainer.appendChild(slidesContainer);

        /* ── Mobile arrow buttons (CSS hides on desktop) ── */
        const prevArrow = document.createElement('button');
        prevArrow.className = 'inline-gal-arrow inline-gal-prev';
        prevArrow.setAttribute('aria-label', 'Previous image');
        prevArrow.innerHTML = '&#8249;';

        const nextArrow = document.createElement('button');
        nextArrow.className = 'inline-gal-arrow inline-gal-next';
        nextArrow.setAttribute('aria-label', 'Next image');
        nextArrow.innerHTML = '&#8250;';

        carouselContainer.appendChild(prevArrow);
        carouselContainer.appendChild(nextArrow);

        /* ── Replace main container content ── */
        mainContainer.innerHTML = '';
        mainContainer.appendChild(carouselContainer);

        /* ── Mobile thumbnail strip (CSS hides on desktop) ── */
        const thumbStrip = document.createElement('div');
        thumbStrip.className = 'inline-gal-thumbs';

        allImages.forEach((src, i) => {
            const thumb = document.createElement('div');
            thumb.className = 'inline-gal-thumb' + (i === 0 ? ' active' : '');
            thumb.dataset.index = String(i);

            const img = document.createElement('img');
            img.src  = src.replace(/width=\d+/, 'width=150');
            img.alt  = `Thumbnail ${i + 1}`;
            img.loading = 'lazy';

            thumb.appendChild(img);
            thumbStrip.appendChild(thumb);

            thumb.addEventListener('click', e => {
                e.stopPropagation();
                currentMainIndex = i;
                updateAll();
            });
        });

        /* Insert strip immediately after the main container */
        mainContainer.parentNode.insertBefore(thumbStrip, mainContainer.nextSibling);

        /* ── Master update function ── */
        function updateAll() {
            /* Slide position */
            slidesContainer.style.transform =
                `translateX(${-currentMainIndex * (100/allImages.length)}%)`;

            /* Desktop 2x2 thumbnail grid */
            thumbnailItems.forEach((t, i) =>
                t.classList.toggle('active', i === currentMainIndex - 1));

            /* Mobile thumbnail strip */
            thumbStrip.querySelectorAll('.inline-gal-thumb').forEach((t, i) =>
                t.classList.toggle('active', i === currentMainIndex));
            const activeThumb = thumbStrip.querySelector('.inline-gal-thumb.active');
            if (activeThumb)
                activeThumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });

            /* Arrow states: hide at first / last slide */
            prevArrow.classList.toggle('hidden', currentMainIndex === 0);
            nextArrow.classList.toggle('hidden', currentMainIndex === allImages.length - 1);
        }

        function next() {
            if (currentMainIndex < allImages.length - 1) { currentMainIndex++; updateAll(); }
        }
        function prev() {
            if (currentMainIndex > 0) { currentMainIndex--; updateAll(); }
        }

        updateAll(); /* set initial state */

        /* ── Arrow click handlers ── */
        prevArrow.addEventListener('click', e => { e.preventDefault(); e.stopPropagation(); prev(); });
        nextArrow.addEventListener('click', e => { e.preventDefault(); e.stopPropagation(); next(); });

        /* ── Desktop: left/right half click ── */
        if (window.innerWidth > 767) {
            carouselContainer.addEventListener('click', e => {
                if (e.target.closest('.inline-gal-arrow')) return;
                e.preventDefault(); e.stopPropagation();
                const rect = carouselContainer.getBoundingClientRect();
                (e.clientX - rect.left < rect.width / 2) ? prev() : next();
            });
        }

        /* ── Desktop thumbnail grid clicks ── */
        thumbnailItems.forEach((thumb, i) => {
            thumb.addEventListener('click', e => {
                e.preventDefault();
                currentMainIndex = i + 1;
                updateAll();
            });
        });

        /* ── Mobile: touch swipe ── */
        const isMobile =
            'ontouchstart' in window || navigator.maxTouchPoints > 0 || window.innerWidth <= 768;

        if (isMobile) {
            let startX = 0, startY = 0, swiping = false;

            /* Prevent carousel tap from opening modal (arrows handle their own clicks) */
            carouselContainer.addEventListener('click', e => {
                if (!e.target.closest('.inline-gal-arrow')) { e.preventDefault(); e.stopPropagation(); }
            });

            carouselContainer.addEventListener('touchstart', e => {
                if (e.touches.length === 1) {
                    startX = e.touches[0].clientX;
                    startY = e.touches[0].clientY;
                    swiping = false;
                }
            }, { passive: true });

            carouselContainer.addEventListener('touchmove', e => {
                if (e.touches.length !== 1) return;
                const dx = Math.abs(e.touches[0].clientX - startX);
                const dy = Math.abs(e.touches[0].clientY - startY);
                if (dx > 30 && !swiping && dx > dy * 1.2) {
                    swiping = true;
                    e.preventDefault();
                    const cur  = -currentMainIndex * (100/allImages.length);
                    const drag = ((e.touches[0].clientX - startX) / carouselContainer.offsetWidth)
                                 * (100/allImages.length);
                    slidesContainer.style.transform = `translateX(${cur + drag}%)`;
                }
            }, { passive: false });

            carouselContainer.addEventListener('touchend', e => {
                if (e.changedTouches.length === 1 && swiping) {
                    const dist = startX - e.changedTouches[0].clientX;
                    if (Math.abs(dist) > 30) { dist > 0 ? next() : prev(); }
                    else { updateAll(); }
                    carouselContainer.style.pointerEvents = 'none';
                    setTimeout(() => { carouselContainer.style.pointerEvents = ''; }, 200);
                }
                setTimeout(() => { swiping = false; }, 350);
            }, { passive: true });
        }
    }
}

/* ================================================================
   GUEST MODAL
   ================================================================ */
function initializeGuestModal() {
    const guestModal   = document.getElementById('guests-modal');
    const guestSelect  = document.getElementById('guests-select');
    const closeBtn     = document.querySelector('.close-guests-btn');
    const guestDisplay = document.getElementById('guests-display');
    const counterBtns  = document.querySelectorAll('.counter-btn');
    let counts = { adults: 1, children: 0, infants: 0, pets: 0 };

    function openModal()  { if (guestModal) { guestModal.classList.add('active'); guestModal.style.display = 'flex'; document.body.classList.add('modal-open'); updateDisplays(); } }
    function closeModal() { if (guestModal) { guestModal.classList.remove('active'); guestModal.style.display = 'none'; document.body.classList.remove('modal-open'); updateGuestDisplay(); } }

    function updateDisplays() {
        Object.keys(counts).forEach(type => {
            const el = document.getElementById(`${type}-count`);
            if (el) el.textContent = counts[type];
        });
        counterBtns.forEach(btn => {
            if (btn.dataset.action === 'decrease') {
                btn.disabled = counts[btn.dataset.type] <= (btn.dataset.type === 'adults' ? 1 : 0);
            }
        });
    }
    function updateGuestDisplay() {
        if (!guestDisplay) return;
        const total = counts.adults + counts.children;
        let text = total <= 1 ? '1 guest' : `${total} guests`;
        if (counts.infants > 0) text += `, ${counts.infants} infant${counts.infants > 1 ? 's' : ''}`;
        if (counts.pets > 0)    text += `, ${counts.pets} pet${counts.pets > 1 ? 's' : ''}`;
        guestDisplay.textContent = text;
    }

    if (guestSelect) guestSelect.addEventListener('click', e => { e.preventDefault(); openModal(); });
    if (closeBtn)    closeBtn.addEventListener('click', closeModal);
    counterBtns.forEach(btn => {
        btn.addEventListener('click', () => {
            const { action, type } = btn.dataset;
            const min = type === 'adults' ? 1 : 0;
            if (action === 'increase') counts[type]++;
            else if (action === 'decrease' && counts[type] > min) counts[type]--;
            updateDisplays();
        });
    });
    if (guestModal) guestModal.addEventListener('click', e => { if (e.target === guestModal) closeModal(); });
    updateGuestDisplay();
    console.log('Guest modal initialized');
}

/* ================================================================
   SHOW MORE BUTTONS / MODALS
   ================================================================ */
function initializeShowMoreButtons() {
    const map = {
        '.show-more-description-btn': '#description-modal',
        '.show-all-amenities-btn':    '#amenities-modal',
        '.show-more-safety-btn':      '#safety-modal',
        '.show-more-cancellation-btn':'#cancellation-modal',
        '.show-all-reviews-btn':      '#reviews-modal',
    };
    function open(modal)  { modal.classList.add('active'); modal.style.display = 'flex'; document.body.classList.add('modal-open'); }
    function close(modal) { modal.classList.remove('active'); modal.style.display = 'none'; document.body.classList.remove('modal-open'); }

    Object.entries(map).forEach(([btnSel, modalSel]) => {
        const modal = document.querySelector(modalSel);
        if (!modal) return;
        document.querySelectorAll(btnSel).forEach(btn => {
            btn.addEventListener('click', e => { e.preventDefault(); open(modal); });
        });
    });
    document.querySelectorAll('.close-modal').forEach(btn => {
        btn.addEventListener('click', e => { e.preventDefault(); const m = btn.closest('.modal'); if (m) close(m); });
    });
    document.querySelectorAll('.modal').forEach(modal => {
        modal.addEventListener('click', e => { if (e.target === modal) close(modal); });
    });
    console.log('Show more buttons initialized');
}

/* ================================================================
   HOSTAWAY BOOKING
   ================================================================ */
function initializeHostAwayBooking() {
    const btns = document.querySelectorAll('.hostaway-btn');
    if (btns.length > 0) {
        btns.forEach(btn => {
            const url = btn.dataset.hostawayUrl || btn.href;
            if (!url || url === '' || url === '#') { btn.style.display = 'none'; return; }
            btn.addEventListener('click', () => console.log('HostAway booking initiated:', url));
            btn.target = '_blank';
            btn.rel    = 'noopener noreferrer';
        });
    }
    document.querySelectorAll('.booking-unavailable-btn').forEach(btn => {
        btn.addEventListener('click', e => { e.preventDefault(); alert('Booking is currently unavailable. Please contact us directly.'); });
    });
}

/* ================================================================
   MAP UTILITIES
   ================================================================ */
function updateAllMapsFromMessage(data) {
    const lat = parseFloat(data.lat), lng = parseFloat(data.lng), zoom = parseInt(data.zoom);
    if (isNaN(lat) || isNaN(lng) || isNaN(zoom)) return;
    document.querySelectorAll('.map-interactive').forEach(container => {
        if (container._leaflet_id) {
            const map = container._leaflet;
            if (map) {
                map.off('moveend');
                map.setView([lat, lng], zoom);
                Object.values(map._layers).forEach(l => { if (l instanceof L.Marker) l.setLatLng([lat, lng]); });
                map.on('moveend', () => { const c = map.getCenter(); updateMapUrlParameters(c.lat, c.lng, map.getZoom()); });
            }
        } else if (container.gmap) {
            container.gmap.setCenter(new google.maps.LatLng(lat, lng));
            container.gmap.setZoom(zoom);
            if (container.marker) container.marker.setPosition(new google.maps.LatLng(lat, lng));
        }
    });
}

function updateMapUrlParameters(lat, lng, zoom) {
    lat  = parseFloat(lat).toFixed(6);
    lng  = parseFloat(lng).toFixed(6);
    zoom = parseInt(zoom);
    const url = new URL(window.location.href);
    url.searchParams.set('lat', lat);
    url.searchParams.set('lng', lng);
    url.searchParams.set('zoom', zoom);
    window.history.replaceState({}, '', url);
    if (window !== window.parent) {
        window.parent.postMessage({ type: 'mapUpdate', lat, lng, zoom }, '*');
    } else {
        document.querySelectorAll('iframe.map-frame').forEach(iframe => {
            try { if (iframe.contentWindow) iframe.contentWindow.postMessage({ type: 'mapUpdate', lat, lng, zoom }, '*'); }
            catch (e) { console.error('Error sending message to iframe:', e); }
        });
    }
}

/* ================================================================
   ADMIN ICON UPLOAD
   ================================================================ */
function initializeAdminIconUpload() {
    const isAdmin = document.body.dataset.userRole === 'admin' || localStorage.getItem('isAdmin') === 'true';
    if (!isAdmin) return;
    console.log('Initializing admin custom icon upload');
    if (!document.getElementById('admin-icon-upload')) createAdminIconInterface();

    const uploadBtn  = document.getElementById('admin-icon-upload');
    const fileInput  = document.getElementById('admin-icon-file-input');
    const applyBtn   = document.getElementById('admin-apply-icon');
    if (uploadBtn && fileInput) {
        uploadBtn.addEventListener('click', () => fileInput.click());
        fileInput.addEventListener('change', handleIconUpload);
        if (applyBtn) applyBtn.addEventListener('click', applyCustomIcon);
    }
    const resetBtn = document.getElementById('admin-reset-icons');
    if (resetBtn) resetBtn.addEventListener('click', resetAllCustomIcons);
    loadExistingCustomIcons();

    function createAdminIconInterface() {
        const el = document.createElement('div');
        el.innerHTML = `<div id="admin-icon-panel" class="admin-icon-panel">
            <h4>Custom Property Detail Icons</h4>
            <div class="admin-icon-controls">
                <select id="icon-type-select" class="admin-select">
                    <option value="">Select icon type...</option>
                    <option value="bedrooms">Bedrooms Icon</option>
                    <option value="beds">Beds Icon</option>
                    <option value="bathrooms">Bathrooms Icon</option>
                    <option value="guests">Guests Icon</option>
                </select>
                <button id="admin-icon-upload" class="admin-btn">Upload Custom Icon</button>
                <input type="file" id="admin-icon-file-input" accept="image/*" style="display:none;">
            </div>
            <div id="admin-icon-preview" class="admin-icon-preview"></div>
            <button id="admin-apply-icon" class="admin-btn admin-btn-primary" style="display:none;">Apply to Property Detail Card</button>
            <button id="admin-reset-icons" class="admin-btn admin-btn-secondary">Reset to Default Icons</button>
        </div>`;
        const grid = document.querySelector('.property-details-grid');
        if (grid) grid.parentNode.insertBefore(el, grid);
        else document.body.appendChild(el);
    }

    function handleIconUpload(event) {
        const file = event.target.files[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) { alert('Please upload a valid image file.'); return; }
        if (file.size > 1024 * 1024) { alert('Image size must be less than 1MB.'); return; }
        const reader = new FileReader();
        reader.onload = e => {
            window.customIconData = e.target.result;
            const prev = document.getElementById('admin-icon-preview');
            if (prev) prev.innerHTML = `<div class="icon-preview-item"><img src="${window.customIconData}" alt="Preview" class="preview-icon"><p>Preview</p></div>`;
            const btn = document.getElementById('admin-apply-icon');
            if (btn) btn.style.display = 'block';
        };
        reader.readAsDataURL(file);
    }

    function applyCustomIcon() {
        const sel = document.getElementById('icon-type-select');
        if (!window.customIconData) { alert('Please upload an icon first.'); return; }
        if (!sel?.value) { alert('Please select an icon type.'); return; }
        localStorage.setItem(`customDetailIcon_${sel.value}`, window.customIconData);
        updatePropertyDetailIcon(sel.value, window.customIconData);
        sel.value = ''; window.customIconData = null;
        const btn = document.getElementById('admin-apply-icon');
        if (btn) btn.style.display = 'none';
        const prev = document.getElementById('admin-icon-preview');
        if (prev) prev.innerHTML = '';
    }
}

function updatePropertyDetailIcon(iconType, iconData) {
    const card = document.querySelector(`[data-detail-type="${iconType}"]`);
    if (!card) return;
    const container = card.querySelector('.detail-icon');
    if (container) container.innerHTML = `<img src="${iconData}" width="24" height="24" alt="${iconType} icon" class="custom-detail-icon">`;
}

function resetAllCustomIcons() {
    ['bedrooms','beds','bathrooms','guests'].forEach(type => {
        localStorage.removeItem(`customDetailIcon_${type}`);
        const card = document.querySelector(`[data-detail-type="${type}"]`);
        if (!card) return;
        const container = card.querySelector('.detail-icon');
        if (!container) return;
        const custom  = container.querySelector('.custom-detail-icon');
        const defIcon = container.querySelector('.default-detail-icon');
        if (custom)  custom.style.display  = 'none';
        if (defIcon) defIcon.style.display = 'block';
    });
    console.log('Custom icons reset');
}

function loadExistingCustomIcons() {
    ['bedrooms','beds','bathrooms','guests'].forEach(type => {
        const saved = localStorage.getItem(`customDetailIcon_${type}`);
        if (saved) updatePropertyDetailIcon(type, saved);
    });
}

/* ================================================================
   INTERACTIVE MAP
   ================================================================ */
function initializeInteractiveMap() {
    console.log('Initializing interactive map');
    const mapContainers = document.querySelectorAll('.map-interactive');
    if (mapContainers.length === 0) return;

    window.addEventListener('message', e => {
        if (e.data?.type === 'mapUpdate') updateAllMapsFromMessage(e.data);
    });

    const urlParams = new URLSearchParams(window.location.search);
    const urlLat = urlParams.get('lat'), urlLng = urlParams.get('lng'), urlZoom = urlParams.get('zoom');

    mapContainers.forEach((container, index) => {
        let lat  = parseFloat(container.dataset.lat);
        let lng  = parseFloat(container.dataset.lng);
        let zoom = parseInt(container.dataset.zoom || '15');
        const title = container.dataset.markerTitle || 'Location';

        if (urlLat && !isNaN(parseFloat(urlLat))) lat  = parseFloat(urlLat);
        if (urlLng && !isNaN(parseFloat(urlLng))) lng  = parseFloat(urlLng);
        if (urlZoom && !isNaN(parseInt(urlZoom))) zoom = parseInt(urlZoom);

        if (window !== window.parent && index === 0) {
            window.parent.postMessage({ type: 'mapUpdate', lat, lng, zoom }, '*');
        }
        if (isNaN(lat) || isNaN(lng)) { console.error('Invalid map coordinates'); return; }

        if (typeof L !== 'undefined') {
            const map    = L.map(container).setView([lat, lng], zoom);
            L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            }).addTo(map);
            L.marker([lat, lng]).addTo(map).bindPopup(title).openPopup();
            map.on('moveend', () => { const c = map.getCenter(); updateMapUrlParameters(c.lat, c.lng, map.getZoom()); });
            console.log('Leaflet map initialized');
        } else if (typeof google !== 'undefined' && typeof google.maps !== 'undefined') {
            const map    = new google.maps.Map(container, { center: { lat, lng }, zoom });
            const marker = new google.maps.Marker({ position: { lat, lng }, map, title });
            map.addListener('idle', () => { const c = map.getCenter(); updateMapUrlParameters(c.lat(), c.lng(), map.getZoom()); });
            container.gmap = map; container.marker = marker;
            console.log('Google Maps initialized');
        } else {
            container.innerHTML = `<div class="map-fallback"><p>Map library not loaded. <a href="https://maps.google.com/?q=${lat},${lng}" target="_blank" rel="noopener">View on Google Maps</a></p></div>`;
        }
    });
}

/* ================================================================
   MOBILE FEATURES
   ================================================================ */
function initializeMobileFeatures() {
    console.log('Mobile features: inline gallery swipe enabled');
}
function initializeMobileGallerySwipe() {
    console.log('Modal gallery swipe disabled — using vertical scroll instead');
}

window.addEventListener('error', e => console.warn('JS error caught:', e.error));
console.log('Airbnb Listing JavaScript fully initialized');